import { CallState } from "@b2bportal/core-types";
import { toDateOrUndefined } from "@b2bportal/core-utilities";
import { AvailabilityResultEnum } from "@b2bportal/homes-api";
import { Amenity, CheckInTimeEnum } from "@b2bportal/lodging-api";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import type { HotelsState } from "../../../store";
import { allHotelRoomsHaveFreeBreakfast } from "../hooks/lodgingFreeBreakfast";
import { getHotelLevelMealPlanKind } from "../hooks/lodgingMealPlans";

export const getHotelShopState = (state: HotelsState) =>
  state.hotels.hotelsShop;

export const getShopLodgingId = (state: HotelsState) =>
  state.hotels.hotelsShop.shopSearch?.lodgingId;

export const getShopSearch = (state: HotelsState) =>
  state.hotels.hotelsShop.shopSearch;

export const getShopCallState = (state: HotelsState) =>
  state.hotels.hotelsShop.shopCallState;

export const getShopAvailabilityCallState = (state: HotelsState) =>
  state.hotels.hotelsShop.availabilityCallState;

export const getSelectedHotel = (state: HotelsState) =>
  state.hotels.hotelsShop.lodging;

export const getHotelShop = (state: HotelsState) =>
  state.hotels.hotelsShop.shop;

export const getHomeShop = (state: HotelsState) =>
  state.hotels.hotelsShop.homesShop;

export const getLodgingSelection = (state: HotelsState) =>
  state.hotels.hotelsShop.lodgingSelection;

export const getSelectedHotelsData = createSelector(
  getHotelShopState,
  (state) => {
    return {
      data: state.lodging,
      isLoading: state.availabilityCallState === CallState.InProcess,
      isError:
        state.availabilityCallState === CallState.Failed ||
        state.shopCallState === CallState.Failed,
      isUnavailable: state.isLodgingUnavailable,
    };
  }
);

export const getShopData = createSelector(getHotelShopState, (state) => {
  return {
    data: state.shop,
    isLoading: state.shopCallState === CallState.InProcess,
    isError: state.shopCallState === CallState.Failed,
  };
});

export const getHomeShopData = createSelector(getHotelShopState, (state) => {
  return {
    data: state.homesShop,
    isLoading: state.shopCallState === CallState.InProcess,
    isError: state.shopCallState === CallState.Failed,
  };
});

export const getHomeListing = createSelector(
  getHomeShopData,
  (homesShop) => homesShop.data?.listing?.listing
);

export const getHomeShopRate = createSelector(getHomeShopData, (homesShop) => {
  const availabilityResult =
    homesShop.data?.listing?.availability?.AvailabilityResult;

  if (availabilityResult === AvailabilityResultEnum.Available) {
    return homesShop.data?.listing?.availability?.rate;
  }

  return null;
});

export const getHomeCancellationPolicy = createSelector(
  getHomeShopRate,
  (rate) => {
    return rate?.cancellationPolicy?.policy;
  }
);

export const getSelectedHotelAmenities = createSelector(
  getSelectedHotel,
  (hotel) => hotel?.lodging?.amenities
);

export const getAmenities = createSelector(
  getSelectedHotel,
  (hotel) => hotel?.lodging.amenities
);

export const getHomeShopAmenities = createSelector(
  getHomeListing,
  (listing) => listing?.content?.amenities ?? []
);

export const getCheckInPolicies = createSelector(
  getSelectedHotel,
  (hotel) => hotel?.lodging.checkInInstructions?.policies
);

export const getCheckInInformation = createSelector(
  getSelectedHotel,
  (hotel) => hotel?.lodging.checkInInstructions
);

export const getCustomerReviews = createSelector(
  getSelectedHotel,
  (hotel) => hotel?.lodging.tripAdvisorReviews
);

export const getHomeReviews = createSelector(
  getHomeListing,
  (listing) => listing?.reviews ?? []
);

export const getRooms = createSelector(getShopData, (shop) => {
  return {
    ...shop,
    data: shop.data?.roomInfoProducts ?? [],
  };
});

export const getAllRoomsHaveFreeBreakfast = createSelector(getRooms, (rooms) =>
  allHotelRoomsHaveFreeBreakfast(rooms.data)
);

export const getHotelMealPlanKind = createSelector(getRooms, (rooms) =>
  getHotelLevelMealPlanKind(rooms.data)
);

export const getHotelHasPaidBreakfast = createSelector(
  getAmenities,
  (amenities) =>
    amenities?.some((a) => a.amenity === Amenity.PaidBreakfast) ?? false
);

export const getShopTrackingProperties = createSelector(
  getShopData,
  (shop) => shop.data?.trackingPropertiesV2 ?? {}
);

export const getHomeShopTrackingProperties = createSelector(
  getHomeListing,
  (listing) => listing?.trackingProperties ?? {}
);

export const getAvailabilityTrackingProperties = createSelector(
  getHotelShopState,
  (shop) => shop.availabilityTrackingProperties ?? {}
);

export const isShopRoomsDataAvailable = createSelector(getShopData, (shop) => {
  return Boolean(shop.data);
});

export const getTotalNights = createSelector(getShopSearch, (search) => {
  if (search == null) return undefined;

  const { dateRange } = search;
  if (dateRange.from != null && dateRange.until != null) {
    const fromDate = dayjs(dateRange.from);
    const untilDate = dayjs(dateRange.until);
    return untilDate.diff(fromDate, "days");
  }
});

export const getSelectedRoom = createSelector(getHotelShopState, (shop) => {
  return shop.selectedRoomProduct;
});

export const getSelectedRoomInfo = createSelector(
  getRooms,
  getSelectedRoom,
  (rooms, selectedRoomRate) => {
    return rooms.data.find((room) =>
      room.products.some((room) => room.rateId === selectedRoomRate?.rateId)
    );
  }
);

export const getSelectedRoomRateId = createSelector(
  getHotelShopState,
  (shop) => {
    return shop.selectedRoomProduct?.rateId?.value;
  }
);

export const getGuests = createSelector(getHotelShopState, (shop) => {
  return shop.shopSearch?.guests;
});

export const getFromDate = createSelector(getShopSearch, (search) => {
  return search?.dateRange.from;
});

export const getUntilDate = createSelector(getShopSearch, (search) => {
  return search?.dateRange.until;
});

export const getRoomsCount = createSelector(getHotelShopState, (shop) => {
  return shop.shopSearch?.rooms;
});

export const getBestOffer = createSelector(getHotelShopState, (shop) => {
  return shop.bestOffer;
});

export const getCheapestRoomProduct = createSelector(getShopData, (shop) => {
  const roomInfoProducts = shop.data?.roomInfoProducts?.filter(
    (room) => (room.products?.length ?? 0) > 0
  );

  if (!roomInfoProducts || roomInfoProducts.length === 0) {
    return {
      data: null,
      isLoading: shop.isLoading,
      isError: shop.isError,
    };
  }

  const cheapestRoomProduct = roomInfoProducts.reduce(
    (currentCheapest, room) => {
      const roomMinPrice = Math.min(
        ...room.products.map((product) => product.perNightTotal.fiat.value)
      );

      if (roomMinPrice < currentCheapest.minPrice) {
        const cheapestProduct = room.products.reduce(
          (cheapestProduct, product) => {
            const productPrice = product.perNightTotal.fiat.value;
            return productPrice < cheapestProduct.perNightTotal.fiat.value
              ? product
              : cheapestProduct;
          },
          room.products[0]
        );

        return {
          product: cheapestProduct,
          minPrice: roomMinPrice,
        };
      }

      return currentCheapest;
    },
    {
      product: roomInfoProducts[0].products[0],
      minPrice: roomInfoProducts[0].products[0].perNightTotal.fiat.value,
    }
  )?.product;

  return {
    data: cheapestRoomProduct,
    isLoading: shop.isLoading,
    isError: shop.isError,
  };
});

export const getSelectedRoomRateCfarQuotes = createSelector(
  [getSelectedRoomInfo, getSelectedRoomRateId],

  (roomInfo, rateId) =>
    roomInfo?.products.find((product) => product.rateId?.value === rateId)
      ?.cfarQuotes
);

export const getSelectedRoomRateCancellationPolicyV1 = createSelector(
  [getSelectedRoomInfo, getSelectedRoomRateId],
  (roomInfo, rateId) =>
    roomInfo?.products.find((product) => product.rateId?.value === rateId)
      ?.cancellationPolicy
);
export const getSelectedRoomRateCancellationPolicyV2 = createSelector(
  [getSelectedRoomInfo, getSelectedRoomRateId],
  (roomInfo, rateId) =>
    roomInfo?.products.find((product) => product.rateId?.value === rateId)
      ?.cancellationPolicyV2
);

export const getCheckInTimestampInHotelTZ = createSelector(
  [getFromDate, getCheckInInformation],
  (from, checkInInfo) => {
    const checkInTime =
      checkInInfo?.checkInTime?.CheckInTime === CheckInTimeEnum.Restricted
        ? checkInInfo.checkInTime.checkInTimeFrom
        : undefined;

    if (from == null || checkInTime == null) {
      return undefined;
    }
    return toDateOrUndefined(`${from}T${checkInTime}`);
  }
);

export const getCheckOutTimestampInHotelTZ = createSelector(
  [getUntilDate, getCheckInInformation],
  (until, checkInInfo) => {
    const checkOutTime = checkInInfo?.checkOutTime;

    if (until == null || checkOutTime == null) {
      return undefined;
    }
    return toDateOrUndefined(`${until}T${checkOutTime}`);
  }
);
